import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { TitleProvider, useBreadcrumbs, useUser } from '../Hooks';
import { ClientList, LoginResponse } from '../screens';
import { Button, Header, Icon, IconName, Modal, Toast } from 'react-frontier';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import { bindClose, bindToggle } from '@payfront/components/Util';
import classNames from 'classnames';
import API from '../API';

import '../style/dashboard.scss';
import UserAccess from '../UserAccess';

interface SiteContainerProps{
	outlet: JSX.Element,
}
interface NavbarItem{
	text: string,
	icon?: IconName,
	url: string,
}

interface SidebarItemProps{
	text: string,
	to?: string,
	extra?: boolean,
	className?: string,
	iconName: IconName,
	access?: number,
	iconStyle?: React.CSSProperties,
	onClick?: ()=>void,
}

const MOBILE_BREAKPOINT = 740;

var SidebarItem = (props: SidebarItemProps)=>{
	var { hasClientAccess } = useUser();
	var location = useLocation();
	
	var active = useMemo(()=>{
		if(!props.to) return false;
		if(props.to==='/'){
			return location.pathname===props.to || location.pathname.startsWith('/pagos');
		}
		return location.pathname.startsWith(props.to);
	}, [location.pathname]);

	if(props.access){
		if(!hasClientAccess(props.access)) return null;
	}

	var Contents = <>
		<Icon name={props.iconName} style={props.iconStyle} />
		<div className="text">{props.text}</div>
	</>

	var item_class = classNames('item', props.className, {
		extra: props.extra,
		active
	})

	if(props.to){
		return <Link className={item_class} to={props.to} onClick={props.onClick}>
			{Contents}
		</Link>
	}else{
		return <div className={item_class} onClick={props.onClick}>
			{Contents}
		</div>
	}
}

var SiteContainer = (props: SiteContainerProps)=>{
	var { t } = useTranslation();
	var [params] = useSearchParams();
	var { user, loaded, loadedError, active_client, admin, live, setActiveClient, setLive, hasClientAccess } = useUser();
	var { breadcrumbs } = useBreadcrumbs();
	var [drawer, setDrawer] = useState<boolean>(false);
	var location = useLocation();
	var navigate = useNavigate();

	useEffect(()=>{
		if(location.pathname==='/auth'){
			return;
		}else if(!user){
			var auth = API.getLoginAuth();
			if(!auth){
				window.location.href = API.getLoginUrl();
			}
		}else if(user){
			var new_client = params.get('cid');
			if(new_client && !Number.isNaN(parseInt(new_client))){
				setActiveClient(parseInt(new_client));
			}
		}
	}, [user, location.pathname]);

	useEffect(()=>{
		setDrawer(false)
	}, [location.pathname, active_client]);

	var showClientList = ()=>{
		navigate('/');
		setActiveClient(null);
	}

	var show_config_divider = useMemo(()=>{
		return hasClientAccess(UserAccess.DEVELOPER.VIEW_WEBHOOKS) || hasClientAccess(UserAccess.USERS.VIEW_USERS);
	}, [user, active_client, loaded]);

	var SidebarContents = useMemo(()=>{
		return <>
			<div className="site">
				<div className="logo">
					<Icon name='money-bill-wave' />
				</div>
				<div className="name">{t('common.site')}</div>
			</div>
			<div className={classNames('divider', { admin: active_client===-1 })}>{t(active_client===-1 ? 'sidebar.admin_divider' : 'sidebar.general_divider')}</div>
			<SidebarItem text={t('sidebar.payments')} iconName='money-bill-wave' to='/' access={UserAccess.PAYMENTS.VIEW_PAYMENTS} />
			<SidebarItem text={t('sidebar.payouts')} iconName='money-check' to='/payouts' access={UserAccess.PAYOUTS.VIEW_PAYOUTS} />
			{active_client===-1 && <>
				<SidebarItem text={t('sidebar.concilia')} iconName='check-double' to='/deposits' />
				<SidebarItem text={t('sidebar.tools')} iconName='wrench' to='/tools' />
			</>}

			{show_config_divider && (
				<div className="divider">{t('sidebar.config_divider')}</div>
			)}
			<SidebarItem text={t('sidebar.users')} iconName='users' to='/users' access={UserAccess.USERS.VIEW_USERS} />
			{active_client!==-1 ? <>
				<SidebarItem text={t('sidebar.webhooks')} iconName='share-alt' to='/webhooks' access={UserAccess.DEVELOPER.VIEW_WEBHOOKS} />
			</> : (
				<SidebarItem text={t('sidebar.clients')} iconName='robot' to='/clients' />
			)}

			<div className="filler"></div>
			{!admin && (
				<SidebarItem extra
					text={t(live ? 'sidebar.live_live' : 'sidebar.live_sandbox')} 
					className={live ? null : 'active'} 
					iconName={live ? 'check-circle' : 'screwdriver-wrench'} 
					iconStyle={{ color: live ? 'green' : '#4747FF' }} 
					access={UserAccess.DEVELOPER.SANDBOX}
					onClick={()=>{
						if(!hasClientAccess(UserAccess.DEVELOPER.SANDBOX)) return;
						setLive(!live)
					}} 
				/>
			)}
			<SidebarItem text={t('sidebar.my_clients')} iconName='reply' extra onClick={showClientList} />
			<SidebarItem text={t('sidebar.logout')} iconName='sign-out' extra onClick={API.logout} />
		</>
	}, [active_client, location, live]);
	if(location.pathname==='/auth') return <LoginResponse />;
	if(!user) return null;
	
	return <TitleProvider suffix={t('common.site')}>
		<Toast.Provider>
			<div className="pf site">
				{loadedError ? (
					<div className="pf placeholder-screen">
						<Header text={t('common.error')} subtext={loadedError} />
						<Button text={t('common.return')} iconName='arrow-left' iconStyle={{ marginRight: 15 }} onClick={showClientList} />
					</div>
				) : !loaded ? (
					<div className='pf placeholder-screen'>
						<Icon name='money-bill-wave'/>
					</div>
				) : !active_client ? (
					<ClientList />
				) : <>
					<div className="site-container">
						<div className="sidebar">
							{SidebarContents}
						</div>
						<div className="contents">
							<div className="navbar">
								{breadcrumbs && breadcrumbs.length>1 ? (
									<Link className='nav' to={breadcrumbs[breadcrumbs.length-2].url}>
										<Icon name='arrow-left' className='disabled' />
									</Link>
								) : (
									<div className="nav disabled">
										<Icon name='arrow-left' className='disabled' />
									</div>
								)}
								<div className="items">
									{!breadcrumbs || breadcrumbs.length==0 ? (
										<div className="item active">
											<Icon name='money-bill-wave' />
											Payfront
										</div>
									) : (
										breadcrumbs.map((a, i, arr)=>(
											<React.Fragment key={`brdcrb-${a.url}-${a.text}`}>
												<Link to={a.url} className={classNames("item", {
													active: i>=(arr.length-1)
												})}>
													{!!a.iconName && <Icon name={a.iconName} />}
													{a.text}
												</Link>
												{!!(i<arr.length-1) && (
													<div className='divider'>/</div>
												)}
											</React.Fragment>
										))
									)}
								</div>
								<div className="drawer" onClick={bindToggle(drawer, setDrawer)}>
									<Icon name='bars' />
								</div>
							</div>
							<div className="contents" id="siteContents">
								{props.outlet}
							</div>
						</div>
						<TransitionGroup component={React.Fragment}>
							{!!drawer && (
								<CSSTransition enter addEndListener={null} timeout={{ appear: 0, exit: 500 }}>
									<div className="overlay" onClick={bindClose(setDrawer, false)}></div>
								</CSSTransition>
							)}
						</TransitionGroup>
						<TransitionGroup component={React.Fragment}>
							{!!drawer && (
								<CSSTransition enter addEndListener={null} timeout={{ appear: 0, exit: 500 }}>
									<div className="pf drawer">
										{SidebarContents}
									</div>
								</CSSTransition>
							)}
						</TransitionGroup>
					</div>
				</>}
			</div>
		</Toast.Provider>
	</TitleProvider>
}

export default SiteContainer;